import { createFileRoute, Link } from "@tanstack/react-router";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy — AdKavi" },
      {
        name: "description",
        content:
          "How AdKavi collects, uses, shares, and protects your data, including third-party AI providers and your privacy rights.",
      },
      { property: "og:title", content: "Privacy Policy — AdKavi" },
      {
        property: "og:description",
        content:
          "Learn how AdKavi handles your data and the AI providers we work with.",
      },
      { property: "og:url", content: "https://adkavi.ai/privacy" },
    ],
    links: [{ rel: "canonical", href: "https://adkavi.ai/privacy" }],
  }),
  component: PrivacyPage,
});

const UPDATED = "April 22, 2026";

function PrivacyPage() {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-3xl mx-auto px-6 py-16">
        <header className="mb-10">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Legal</p>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mt-2">Privacy Policy</h1>
          <p className="mt-3 text-sm text-muted-foreground">Last updated: {UPDATED}</p>
        </header>

        <div className="space-y-8 text-sm leading-relaxed">
          <Section title="1. Introduction">
            <p>
              This Privacy Policy explains how AdKavi ("AdKavi", "we", "us") collects, uses,
              shares, and protects your personal data when you use the AdKavi website and
              services (the "Service"). By using the Service, you agree to this Policy.
            </p>
          </Section>

          <Section title="2. Data We Collect">
            <ul className="list-disc pl-5 space-y-1.5">
              <li>
                <strong>Account data:</strong> name, email address, password hash, profile
                preferences, and authentication metadata.
              </li>
              <li>
                <strong>Billing data:</strong> subscription plan, billing status, and limited
                payment metadata. Card details are handled directly by our payments processor —
                we do not store full card numbers on our servers.
              </li>
              <li>
                <strong>Content data:</strong> campaigns, ad copy, creative assets, scheduled
                posts, prompts, AI-generated outputs, and other content you create or upload.
              </li>
              <li>
                <strong>Integration data:</strong> when you connect a third-party platform
                (e.g. Meta), we receive the account identifiers and tokens needed to operate
                the integration on your behalf.
              </li>
              <li>
                <strong>Usage data:</strong> log data, device and browser information, IP
                address, pages viewed, and basic interaction events used for security and
                product improvement.
              </li>
              <li>
                <strong>Cookies & similar tech:</strong> used for authentication, preferences,
                and basic analytics. You can control cookies through your browser settings.
              </li>
            </ul>
          </Section>

          <Section title="3. How We Use Your Data">
            <ul className="list-disc pl-5 space-y-1.5">
              <li>To provide, maintain, and secure the Service.</li>
              <li>To process subscriptions, billing, and customer support.</li>
              <li>To generate AI outputs you request, including via third-party AI providers.</li>
              <li>To publish or schedule content to platforms you have connected.</li>
              <li>To send service communications (e.g. receipts, security alerts, important updates).</li>
              <li>To analyze usage in aggregate to improve features and reliability.</li>
              <li>To comply with legal obligations and enforce our Terms.</li>
            </ul>
          </Section>

          <Section title="4. AI Processing">
            <p>
              When you use AI features, the prompts and inputs you submit (which may include
              the content you provide) are sent to AI providers to generate the requested
              output. AI Output may be inaccurate or unintended; you are responsible for
              reviewing it before publishing.
            </p>
            <p>
              We do not sell your prompts. We do not use your private content to train
              third-party foundation models without your consent; however, AI providers may
              process inputs in line with their own policies (see Section 6).
            </p>
          </Section>

          <Section title="5. Legal Bases (EEA / UK)">
            <p>If you are in the EEA or UK, we process personal data on these bases:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li><strong>Contract:</strong> to provide the Service you signed up for.</li>
              <li><strong>Legitimate interests:</strong> to secure, improve, and market the Service in a way that is not overridden by your rights.</li>
              <li><strong>Consent:</strong> for optional cookies, marketing emails, and certain integrations.</li>
              <li><strong>Legal obligation:</strong> to meet accounting, tax, and regulatory requirements.</li>
            </ul>
          </Section>

          <Section title="6. Third-Party Providers">
            <p>
              AdKavi is powered by third-party APIs and infrastructure. The main categories,
              and examples of providers we currently use, are:
            </p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li><strong>AI / generative models:</strong> OpenAI, Anthropic (Claude), HeyGen, ElevenLabs.</li>
              <li><strong>Advertising & social platforms:</strong> Meta (Facebook, Instagram).</li>
              <li><strong>Cloud infrastructure & databases</strong> used to host the Service.</li>
              <li><strong>Payments processor</strong> for subscription billing.</li>
              <li><strong>Email and analytics providers</strong> for transactional emails and basic product analytics.</li>
            </ul>
            <p>
              These providers process data on our behalf or as independent controllers under
              their own terms. We choose providers that offer reasonable security and privacy
              practices, but their processing is governed by their own privacy policies.
            </p>
          </Section>

          <Section title="7. Sharing Your Data">
            <p>We share personal data only:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>With service providers described above, under contractual safeguards.</li>
              <li>With platforms you explicitly connect (e.g. publishing a post to Instagram).</li>
              <li>To comply with law, valid legal process, or to protect rights, safety, and security.</li>
              <li>In connection with a merger, acquisition, or sale of assets, with notice where required.</li>
            </ul>
            <p>We do not sell your personal data.</p>
          </Section>

          <Section title="8. International Transfers">
            <p>
              Your data may be processed in countries other than your own, including the United
              States. Where required, we rely on appropriate safeguards such as Standard
              Contractual Clauses to protect international transfers.
            </p>
          </Section>

          <Section title="9. Data Retention">
            <p>
              We retain personal data for as long as your account is active and as needed to
              provide the Service, comply with legal obligations, resolve disputes, and enforce
              our agreements. When you delete your account, we delete or anonymize personal
              data within a reasonable timeframe, except where retention is required by law.
            </p>
          </Section>

          <Section title="10. Security">
            <p>
              We use industry-standard technical and organizational measures (encryption in
              transit, access controls, authentication, audit logging) to protect your data. No
              method of transmission or storage is 100% secure; we cannot guarantee absolute
              security.
            </p>
          </Section>

          <Section title="11. Your Rights">
            <p>Depending on your jurisdiction, you may have the right to:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Access, correct, or delete your personal data.</li>
              <li>Object to or restrict certain processing.</li>
              <li>Port your data to another service.</li>
              <li>Withdraw consent where processing is based on consent.</li>
              <li>Lodge a complaint with your local data protection authority.</li>
            </ul>
            <p>
              To exercise these rights, email{" "}
              <a href="mailto:support@adkavi.com" className="text-primary underline">
                support@adkavi.com
              </a>
              . We may need to verify your identity before responding.
            </p>
          </Section>

          <Section title="12. Children">
            <p>
              The Service is not directed to children under 16, and we do not knowingly collect
              personal data from them. If you believe a child has provided us personal data,
              please contact us so we can delete it.
            </p>
          </Section>

          <Section title="13. Changes to This Policy">
            <p>
              We may update this Policy from time to time. If changes are material, we will
              provide reasonable notice. Your continued use of the Service after the effective
              date constitutes acceptance of the updated Policy.
            </p>
          </Section>

          <Section title="14. Contact">
            <p>
              For privacy questions or requests, email{" "}
              <a href="mailto:support@adkavi.com" className="text-primary underline">
                support@adkavi.com
              </a>
              . See also our{" "}
              <Link to="/terms" className="text-primary underline">
                Terms of Service
              </Link>
              .
            </p>
          </Section>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-xl font-semibold tracking-tight mb-3">{title}</h2>
      <div className="space-y-3 text-muted-foreground">{children}</div>
    </section>
  );
}
